'use client'
import React, { useState, useEffect, useRef } from 'react'
import { cn } from '@/utilities/ui'
import Slider from 'react-slick'
import Link from 'next/link'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import type { FeaturedAgencyBlock as FeaturedAgencyBlockProps } from 'src/payload-types'

type Props = {
  className?: string
  dictionary?: string
  autoScrollInterval?: number // Auto-scroll interval in milliseconds (default: 3000)
} & FeaturedAgencyBlockProps

// Agency Card Component with conditional sizing
const AgencyCard = ({ agency, isCenter = false, isMobile = false }) => (
  <Link href={`/agencies/${agency.slug}`} className="block">
    <div className="flex flex-col items-center px-2 hover:opacity-90 transition-all duration-300">
      <div
        className={cn(
          'bg-white rounded-full flex items-center justify-center mb-3 shadow-sm transition-all duration-500 ease-out overflow-hidden',
          isMobile
            ? 'w-28 h-28 sm:w-32 sm:h-32'
            : isCenter
              ? 'w-36 h-36 lg:w-40 lg:h-40 shadow-lg transform scale-110'
              : 'w-28 h-28 lg:w-32 lg:h-32',
        )}
      >
        {agency.Media?.url ? (
          <img
            src={agency.Media.url}
            alt={agency.title || 'Agency logo'}
            className="w-full h-full object-cover rounded-full transition-all duration-500 ease-out"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 rounded-full"></div>
        )}
      </div>
      <h3
        className={cn(
          'text-typography font-medium text-center transition-all duration-500 ease-out',
          isMobile ? 'text-lg' : isCenter ? 'text-xl lg:text-2xl font-semibold' : 'text-lg',
        )}
      >
        {agency.title}
      </h3>
    </div>
  </Link>
)

// Custom Arrow Components
const CustomPrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute hidden left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105  items-center justify-center"
  >
    <svg width="6" height="10" viewBox="0 0 8 14" fill="none" className="ml-0.5">
      <path
        d="M7 1L1 7L7 13"
        stroke="#333"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
)

const CustomNextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-4 top-1/2 hidden transform -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105  items-center justify-center"
  >
    <svg width="6" height="10" viewBox="0 0 8 14" fill="none" className="mr-0.5">
      <path
        d="M1 1L7 7L1 13"
        stroke="#333"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
)

// Desktop Carousel Component with React Slick
const DesktopCarousel = ({ agencies, autoScrollInterval = 3000 }) => {
  const sliderRef = useRef(null)
  const [isPaused, setIsPaused] = useState(false)

  // Auto-scroll functionality with hover pause
  useEffect(() => {
    if (isPaused) return

    const interval = setInterval(() => {
      if (sliderRef.current) {
        sliderRef.current.slickNext()
      }
    }, autoScrollInterval)

    return () => clearInterval(interval)
  }, [isPaused, autoScrollInterval])

  // Mouse enter/leave handlers for pause on hover
  const handleMouseEnter = () => {
    setIsPaused(true)
  }

  const handleMouseLeave = () => {
    setIsPaused(false)
  }

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 700,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '0px',
    cssEase: 'ease-in-out',
    autoplay: false, // We handle autoplay manually
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          centerMode: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: '60px',
        },
      },
    ],
  }

  return (
    <div
      className="relative agency-carousel-desktop"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Slider ref={sliderRef} {...sliderSettings}>
        {agencies.map((agency) => (
          <div key={agency.id} className="px-4">
            <AgencyCard agency={agency} isMobile={false} />
          </div>
        ))}
      </Slider>

      {/* Custom CSS for center mode styling */}
      <style jsx>{`
        .agency-carousel-desktop :global(.slick-slide) {
          opacity: 0.6;
          transform: scale(0.9);
          transition: all 0.5s ease-in-out;
        }

        .agency-carousel-desktop :global(.slick-slide.slick-center) {
          opacity: 1;
          transform: scale(1.1);
          z-index: 10;
        }

        .agency-carousel-desktop :global(.slick-slide.slick-center .animate-pulse-subtle) {
          animation: pulse-subtle 2s ease-in-out infinite;
        }

        @keyframes pulse-subtle {
          0%,
          100% {
            transform: scale(1.1);
          }
          50% {
            transform: scale(1.12);
          }
        }

        .agency-carousel-desktop :global(.slick-list) {
          overflow: visible;
        }

        .agency-carousel-desktop :global(.slick-track) {
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  )
}

// Mobile Carousel with React Slick
const MobileCarousel = ({ agencies, autoScrollInterval = 3000 }) => {
  const sliderRef = useRef(null)

  // Auto-scroll for mobile
  useEffect(() => {
    const interval = setInterval(() => {
      if (sliderRef.current) {
        sliderRef.current.slickNext()
      }
    }, autoScrollInterval)

    return () => clearInterval(interval)
  }, [autoScrollInterval])

  const mobileSliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 700,
    slidesToShow: 2,
    slidesToScroll: 1,
    centerMode: false,
    variableWidth: false,
    cssEase: 'ease-in-out',
    autoplay: false, // We handle autoplay manually
  }

  return (
    <Slider ref={sliderRef} {...mobileSliderSettings} className="agency-slider">
      {agencies.map((agency) => (
        <div key={agency.id} className="px-2">
          <AgencyCard agency={agency} isMobile={true} />
        </div>
      ))}
    </Slider>
  )
}

export const FeaturedAgencyBlock = ({
  className,
  dictionary,
  agencies,
  autoScrollInterval = 3000,
}: Props) => {
  // If no agencies are selected, don't display anything
  if (!agencies || agencies.length === 0) {
    return null
  }

  return (
    <div className={cn('py-4 bg-light', className)}>
      <div className="container mx-auto px-4">
        <h2 className="text-typography md:text-center md:mb-14  sm:text-h2 text-mobile-h2 font-semibold mb-4">
          {dictionary?.propertyDetail?.agencyList.agencies}
        </h2>

        {/* Mobile carousel view */}
        <div className="md:hidden !h-[180px] sm:!h-[200px]">
          <MobileCarousel agencies={agencies} autoScrollInterval={autoScrollInterval} />
        </div>

        {/* Desktop carousel with center mode */}
        <div className="hidden md:block min-h-[280px]">
          <DesktopCarousel agencies={agencies} autoScrollInterval={autoScrollInterval} />
        </div>
      </div>
    </div>
  )
}
